import { useTranslation } from "react-i18next";

import { MdmPolicyCommand } from "@devices/types";
import { MinimalTable, MinimalTableHeader, MinimalTableHeaderCell, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { PaneElement } from "@sidePanel/shared/PaneElement";
import { ColumnDef, getCoreRowModel, Table, useReactTable } from "@tanstack/react-table";

import PolicyCommandItem from "./PolicyCommandItem";
import { SortablePolicyCommandsBody } from "./SortablePolicyCommandsBody.lazy";

interface PolicyCommandsTableProps {
    content: MdmPolicyCommand[];
    onUpdate?: (content: MdmPolicyCommand[]) => Promise<boolean>;
    readOnly?: boolean;
    loading?: boolean;
}

interface PolicyCommandsTableBodyProps {
    content: MdmPolicyCommand[];
    onUpdate?: (content: MdmPolicyCommand[]) => Promise<boolean>;
    readOnly: boolean;
    loading: boolean;
    table: Table<MdmPolicyCommand>;
    noDataMessage: string;
}

const columns: ColumnDef<MdmPolicyCommand>[] = [{ id: "drag" }, { id: "index" }, { id: "command" }, { id: "actions" }];

/**
 * Rows are reorderable and deletable, and each one owns local state (expanded details, open edit and delete
 * dialogs) plus a form that outlives the dialog. Keying by array index hands that state to whichever rule later
 * occupies the slot, so dragging a rule past an expanded one leaves the expansion — and any unsaved JSON in its
 * edit dialog — attached to a different rule.
 *
 * `API.Policy` is `Record<string, string | number> & { id?: never }`, so there is no id to key on and the identity
 * has to come from the content itself. Identical rules are disambiguated by how often they have appeared, which is
 * stable under reordering: two rules with the same content are interchangeable anyway.
 */
const getRowKeys = (content: MdmPolicyCommand[]): string[] => {
    const seen = new Map<string, number>();

    return content.map((command) => {
        const identity = JSON.stringify(command);
        const occurrence = seen.get(identity) ?? 0;
        seen.set(identity, occurrence + 1);

        return occurrence === 0 ? identity : `${identity}#${occurrence}`;
    });
};

const PolicyCommandsTableBody = ({
    content,
    onUpdate,
    readOnly,
    loading,
    table,
    noDataMessage,
}: PolicyCommandsTableBodyProps) => {
    const rowKeys = getRowKeys(content);

    if (readOnly) {
        return (
            <TableBody table={table} noDataMessage={noDataMessage}>
                {content.map((c, index) => (
                    <PolicyCommandItem index={index} content={content} policyCommand={c} key={rowKeys[index]} readOnly />
                ))}
            </TableBody>
        );
    }

    if (!content.length) {
        return <TableBody table={table} noDataMessage={noDataMessage} children={null} />;
    }

    return <SortablePolicyCommandsBody content={content} rowKeys={rowKeys} onUpdate={onUpdate} loading={loading} />;
};

const PolicyCommandsTable = ({ content, onUpdate, readOnly = false, loading = false }: PolicyCommandsTableProps) => {
    const { t } = useTranslation(["sidepanels", "pages"]);

    const table = useReactTable<MdmPolicyCommand>({ data: content, columns, getCoreRowModel: getCoreRowModel() });

    return (
        <PaneElement.TableLayout contentDeps={[content.length]}>
            <MinimalTable stickyHeader>
                <MinimalTableHeader>
                    <MinimalTableRow>
                        <MinimalTableHeaderCell width={50} />
                        <MinimalTableHeaderCell align="center">
                            {t("sidepanels:groupPanel.table.header.index")}
                        </MinimalTableHeaderCell>
                        <MinimalTableHeaderCell align="left">{t("pages:policiesPage.table.header.rule")}</MinimalTableHeaderCell>
                        <MinimalTableHeaderCell align="right" />
                    </MinimalTableRow>
                </MinimalTableHeader>
                <PolicyCommandsTableBody
                    content={content}
                    onUpdate={onUpdate}
                    readOnly={readOnly}
                    loading={loading}
                    table={table}
                    noDataMessage={t("pages:policiesPage.section.rules.empty")}
                />
            </MinimalTable>
        </PaneElement.TableLayout>
    );
};

export default PolicyCommandsTable;
